// worklistUtils.ts — pure helpers for the claims desk worklist (wireframe
// S2, Insurance Claims Phase 4.5.2): the "needs action" default sort and a
// short next-action hint per claim. Both work off fields the list endpoint
// already returns (tat, enhancement, status) — no extra fetch per row.
import type { Claim } from './types';

/** Lower sorts first. 0 = TAT breached, 1 = due today/soon (an amber TAT
 * signal that hasn't breached yet, or an enhancement that's come due),
 * 2 = has a live TAT clock but neither of the above, 3 = nothing urgent. */
function urgencyRank(c: Claim): 0 | 1 | 2 | 3 {
  if (c.tat_breached) return 0;
  const amber = c.tat?.authorization?.amber || (c.tat?.query && !c.tat.query.replied && c.tat.query.hours_remaining != null && c.tat.query.hours_remaining <= 24);
  if (amber || c.enhancement?.due) return 1;
  if (c.tat?.authorization || c.tat?.query || c.tat?.submission_ageing) return 2;
  return 3;
}

/** The wireframe's own default sort: "breached TAT, then due today, then
 * the rest by age" — age within a bucket is oldest-first (the longer a
 * claim has sat untouched, the sooner the desk should look at it). */
export function sortByNeedsAction(claims: Claim[]): Claim[] {
  return [...claims].sort((a, b) => {
    const r = urgencyRank(a) - urgencyRank(b);
    if (r !== 0) return r;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

const STATUS_NEXT_ACTION: Partial<Record<Claim['status'], string>> = {
  draft: 'Send pre-authorisation',
  preauth_sent: 'Await pre-auth decision',
  preauth_query: 'Reply to pre-auth query',
  preauth_approved: 'Admission in progress',
  preauth_denied: 'Converted to self-pay',
  enhancement_sent: 'Await enhancement decision',
  final_auth_sent: 'Await final approval',
  final_auth_query: 'Reply to final-auth query',
  final_approved: 'Patient ready — collect & discharge',
  discharged: 'Build packet & submit',
  submitted: 'Awaiting payer response',
  claim_query: 'Reply to claim query',
  settled: 'Closed out',
  short_settled: 'Review deductions',
  rejected: 'Review rejection',
  appealed: 'Awaiting appeal outcome',
  closed: 'Closed',
  cancelled: 'Cancelled',
  documents_issued: 'Documents issued to patient',
};

/** A short "what to do next" hint — advisory text only, never a link
 * target; the desk still opens the claim to act. */
export function nextActionFor(c: Claim): string {
  if (c.tat_breached) return 'Chase TPA — TAT breached';
  if (c.enhancement?.due) return 'Raise enhancement (bill ≥ threshold of approved)';
  if (c.tat?.query && !c.tat.query.replied && c.tat.query.hours_remaining != null && c.tat.query.hours_remaining <= 24) {
    return `Reply to query (${c.tat.query.hours_remaining}h left)`;
  }
  return STATUS_NEXT_ACTION[c.status] ?? '—';
}
